import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode, getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { Rect } from "@/lib/math/rect";
import {
  createSceneTransformCache,
  nodeSelectionLocalToWorldMatrix,
  type SceneTransformCache,
} from "@/document/operations/nodeWorldTransform";
import { applyAffinePoint } from "@/lib/math/affine";
import { sampleEdgeBezier, type Point } from "@/document/operations/vectorGeometry";

export interface MeasureOverlayLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  dashed?: boolean;
}

export interface MeasureOverlayLabel {
  x: number;
  y: number;
  text: string;
}

export interface MeasureOverlay {
  lines: MeasureOverlayLine[];
  labels: MeasureOverlayLabel[];
}

const EPSILON = 0.01;

export function isMeasureModifierActive(event: Pick<KeyboardEvent, "altKey" | "shiftKey">): boolean {
  return event.altKey && !event.shiftKey;
}

export function isSelectionContainer(node: SceneNode): boolean {
  return node.type === "frame" ||
    node.type === "group" ||
    node.type === "boolean" ||
    node.type === "component" ||
    node.type === "componentVariant" ||
    node.type === "instance";
}

export function formatMeasureDistance(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return String(parseFloat(rounded.toFixed(2)));
}

export function computeMeasureOverlay(
  document: CurvaDocument,
  selectedNodeIds: string[],
  selectionBounds: Rect,
  hoveredNodeId: string | undefined,
): MeasureOverlay | undefined {
  if (selectedNodeIds.length === 0) {
    return undefined;
  }

  const selectedIds = new Set(selectedNodeIds);
  const measureSelectionBounds = getMeasureSelectionBounds(document, selectedNodeIds) ?? selectionBounds;

  if (!hoveredNodeId || selectedIds.has(hoveredNodeId)) {
    const parentBounds = nearestParentContainerBounds(document, selectedNodeIds);
    return parentBounds ? measureToContainerEdges(measureSelectionBounds, parentBounds) : undefined;
  }

  const hoveredNode = findSceneNode(document, hoveredNodeId);
  if (!hoveredNode) {
    const parentBounds = nearestParentContainerBounds(document, selectedNodeIds);
    return parentBounds ? measureToContainerEdges(measureSelectionBounds, parentBounds) : undefined;
  }

  return measureBetweenRects(measureSelectionBounds, getNodeMeasureBounds(document, hoveredNode));
}

/**
 * Geometric world AABB for measuring — not hit-test interaction bounds.
 * Vector networks expand interaction bounds by ≥3px for broad-phase hit testing;
 * measuring must use the true selection/geometry box so guides touch the shape.
 */
export function getNodeMeasureBounds(
  document: CurvaDocument,
  node: SceneNode,
  transformCache: SceneTransformCache = createSceneTransformCache(document),
): Rect {
  return transformCache.interactionBounds(node);
}

function getMeasureSelectionBounds(document: CurvaDocument, selectedNodeIds: string[]): Rect | undefined {
  const nodes = selectedNodeIds
    .map(nodeId => findSceneNode(document, nodeId))
    .filter((node): node is SceneNode => node !== undefined);
  if (nodes.length === 0) {
    return undefined;
  }

  const cache = createSceneTransformCache(document);
  const bounds = nodes.map(node => getNodeMeasureBounds(document, node, cache));
  const left = Math.min(...bounds.map(rect => rect.x));
  const top = Math.min(...bounds.map(rect => rect.y));
  const right = Math.max(...bounds.map(rect => rect.x + rect.width));
  const bottom = Math.max(...bounds.map(rect => rect.y + rect.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function nearestParentContainerBounds(document: CurvaDocument, nodeIds: string[]): Rect | undefined {
  const parents = nodeIds.map(nodeId => findDirectParentContainer(document, nodeId));
  const first = parents[0];
  if (!first || parents.some(parent => parent?.id !== first.id)) {
    return undefined;
  }

  return getNodeMeasureBounds(document, first);
}

export function findDirectParentContainer(document: CurvaDocument, nodeId: string): SceneNode | undefined {
  for (const page of document.pages) {
    const parent = findDirectParentContainerInNodes(page.children, nodeId, undefined);
    if (parent !== NOT_FOUND) {
      return parent;
    }
  }

  return undefined;
}

const NOT_FOUND = Symbol("not-found");

function findDirectParentContainerInNodes(
  nodes: SceneNode[],
  nodeId: string,
  parentContainer: SceneNode | undefined,
): SceneNode | undefined | typeof NOT_FOUND {
  for (const node of nodes) {
    if (node.id === nodeId) {
      return parentContainer;
    }

    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    const nextParent = isSelectionContainer(node) ? node : parentContainer;
    const found = findDirectParentContainerInNodes(children, nodeId, nextParent);
    if (found !== NOT_FOUND) {
      return found;
    }
  }

  return NOT_FOUND;
}

export function measureBetweenRects(source: Rect, target: Rect): MeasureOverlay {
  const lines: MeasureOverlayLine[] = [];
  const labels: MeasureOverlayLabel[] = [];

  const sourceEdges = rectEdges(source);
  const targetEdges = rectEdges(target);

  const horizontalGap = horizontalSeparationGap(sourceEdges, targetEdges);
  const verticalGap = verticalSeparationGap(sourceEdges, targetEdges);

  if (horizontalGap !== undefined && verticalGap !== undefined) {
    addDiagonalCornerMeasurement(lines, labels, sourceEdges, targetEdges, horizontalGap, verticalGap);
    return { lines, labels };
  }

  if (horizontalGap !== undefined) {
    addHorizontalGapMeasurement(lines, labels, sourceEdges, targetEdges, horizontalGap);
    addHorizontalAlignmentOffsets(lines, labels, sourceEdges, targetEdges, horizontalGap.sourceIsLeft);
  }

  if (verticalGap !== undefined) {
    addVerticalGapMeasurement(lines, labels, sourceEdges, targetEdges, verticalGap);
    addVerticalAlignmentOffsets(lines, labels, sourceEdges, targetEdges, verticalGap.sourceIsAbove);
  }

  if (horizontalGap === undefined && verticalGap === undefined) {
    addOverlappingEdgeMeasurements(lines, labels, sourceEdges, targetEdges);
  }

  return { lines, labels };
}

export function measureToContainerEdges(selection: Rect, container: Rect): MeasureOverlay {
  const lines: MeasureOverlayLine[] = [];
  const labels: MeasureOverlayLabel[] = [];
  const selectionEdges = rectEdges(selection);
  const containerEdges = rectEdges(container);

  addInsetMeasurement(lines, labels, containerEdges.left, selectionEdges.left, selectionEdges.top, selectionEdges.bottom, "horizontal");
  addInsetMeasurement(lines, labels, selectionEdges.right, containerEdges.right, selectionEdges.top, selectionEdges.bottom, "horizontal");
  addInsetMeasurement(lines, labels, containerEdges.top, selectionEdges.top, selectionEdges.left, selectionEdges.right, "vertical");
  addInsetMeasurement(lines, labels, selectionEdges.bottom, containerEdges.bottom, selectionEdges.left, selectionEdges.right, "vertical");

  return { lines, labels };
}

interface RectEdges {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
}

function rectEdges(rect: Rect): RectEdges {
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  return {
    left,
    top,
    right,
    bottom,
    centerX: left + rect.width / 2,
    centerY: top + rect.height / 2,
  };
}

function horizontalSeparationGap(source: RectEdges, target: RectEdges): { distance: number; sourceIsLeft: boolean } | undefined {
  if (source.right <= target.left) {
    return { distance: target.left - source.right, sourceIsLeft: true };
  }
  if (target.right <= source.left) {
    return { distance: source.left - target.right, sourceIsLeft: false };
  }
  return undefined;
}

function verticalSeparationGap(source: RectEdges, target: RectEdges): { distance: number; sourceIsAbove: boolean } | undefined {
  if (source.bottom <= target.top) {
    return { distance: target.top - source.bottom, sourceIsAbove: true };
  }
  if (target.bottom <= source.top) {
    return { distance: source.top - target.bottom, sourceIsAbove: false };
  }
  return undefined;
}

function overlapCenter(minA: number, maxA: number, minB: number, maxB: number, fallback: number): number {
  const start = Math.max(minA, minB);
  const end = Math.min(maxA, maxB);
  return end > start ? (start + end) / 2 : fallback;
}

function addHorizontalGapMeasurement(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
  gap: { distance: number; sourceIsLeft: boolean },
): void {
  const left = gap.sourceIsLeft ? source : target;
  const right = gap.sourceIsLeft ? target : source;
  const y = overlapCenter(source.top, source.bottom, target.top, target.bottom, (source.centerY + target.centerY) / 2);
  const x1 = left.right;
  const x2 = right.left;
  lines.push({ x1, y1: y, x2, y2: y });
  labels.push({ x: (x1 + x2) / 2, y, text: formatMeasureDistance(gap.distance) });
}

function addVerticalGapMeasurement(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
  gap: { distance: number; sourceIsAbove: boolean },
): void {
  const top = gap.sourceIsAbove ? source : target;
  const bottom = gap.sourceIsAbove ? target : source;
  const x = overlapCenter(source.left, source.right, target.left, target.right, (source.centerX + target.centerX) / 2);
  const y1 = top.bottom;
  const y2 = bottom.top;
  lines.push({ x1: x, y1, x2: x, y2 });
  labels.push({ x, y: (y1 + y2) / 2, text: formatMeasureDistance(gap.distance) });
}

function addDiagonalCornerMeasurement(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
  horizontalGap: { distance: number; sourceIsLeft: boolean },
  verticalGap: { distance: number; sourceIsAbove: boolean },
): void {
  const sourceCornerX = horizontalGap.sourceIsLeft ? source.right : source.left;
  const sourceCornerY = verticalGap.sourceIsAbove ? source.bottom : source.top;
  const targetCornerX = horizontalGap.sourceIsLeft ? target.left : target.right;
  const targetCornerY = verticalGap.sourceIsAbove ? target.top : target.bottom;
  const elbowX = targetCornerX;
  const elbowY = sourceCornerY;

  lines.push({ x1: sourceCornerX, y1: elbowY, x2: elbowX, y2: elbowY });
  labels.push({ x: (sourceCornerX + elbowX) / 2, y: elbowY, text: formatMeasureDistance(horizontalGap.distance) });

  lines.push({ x1: elbowX, y1: elbowY, x2: elbowX, y2: targetCornerY });
  labels.push({ x: elbowX, y: (elbowY + targetCornerY) / 2, text: formatMeasureDistance(verticalGap.distance) });

  const sourceVerticalEdgeY = verticalGap.sourceIsAbove ? source.top : source.bottom;
  lines.push({ x1: sourceCornerX, y1: sourceVerticalEdgeY, x2: sourceCornerX, y2: sourceCornerY, dashed: true });

  const sourceHorizontalEdgeX = horizontalGap.sourceIsLeft ? source.left : source.right;
  lines.push({ x1: sourceHorizontalEdgeX, y1: sourceCornerY, x2: sourceCornerX, y2: sourceCornerY, dashed: true });
}

function addHorizontalAlignmentOffsets(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
  sourceIsLeft: boolean,
): void {
  const left = sourceIsLeft ? source : target;
  const right = sourceIsLeft ? target : source;
  const xMeasure = (left.right + right.left) / 2;

  if (Math.abs(left.top - right.top) > EPSILON) {
    addVerticalEdgeOffsetGuide(lines, labels, xMeasure, left.right, right.left, left.top, right.top);
  }
  if (Math.abs(left.bottom - right.bottom) > EPSILON) {
    addVerticalEdgeOffsetGuide(lines, labels, xMeasure, left.right, right.left, left.bottom, right.bottom);
  }
}

function addVerticalAlignmentOffsets(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
  sourceIsAbove: boolean,
): void {
  const top = sourceIsAbove ? source : target;
  const bottom = sourceIsAbove ? target : source;
  const yMeasure = (top.bottom + bottom.top) / 2;

  if (Math.abs(top.left - bottom.left) > EPSILON) {
    addHorizontalEdgeOffsetGuide(lines, labels, yMeasure, top.bottom, bottom.top, top.left, bottom.left);
  }
  if (Math.abs(top.right - bottom.right) > EPSILON) {
    addHorizontalEdgeOffsetGuide(lines, labels, yMeasure, top.bottom, bottom.top, top.right, bottom.right);
  }
}

function addHorizontalEdgeOffsetGuide(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  yMeasure: number,
  yTop: number,
  yBottom: number,
  xTop: number,
  xBottom: number,
): void {
  const xMin = Math.min(xTop, xBottom);
  const xMax = Math.max(xTop, xBottom);
  const distance = xMax - xMin;
  if (distance <= EPSILON) {
    return;
  }

  lines.push({ x1: xMin, y1: yMeasure, x2: xMax, y2: yMeasure });
  lines.push({ x1: xTop, y1: yTop, x2: xTop, y2: yMeasure, dashed: true });
  lines.push({ x1: xBottom, y1: yBottom, x2: xBottom, y2: yMeasure, dashed: true });
  labels.push({ x: (xMin + xMax) / 2, y: yMeasure, text: formatMeasureDistance(distance) });
}

function addVerticalEdgeOffsetGuide(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  xMeasure: number,
  xLeft: number,
  xRight: number,
  yLeft: number,
  yRight: number,
): void {
  const yMin = Math.min(yLeft, yRight);
  const yMax = Math.max(yLeft, yRight);
  const distance = yMax - yMin;
  if (distance <= EPSILON) {
    return;
  }

  lines.push({ x1: xMeasure, y1: yMin, x2: xMeasure, y2: yMax });
  lines.push({ x1: xLeft, y1: yLeft, x2: xMeasure, y2: yLeft, dashed: true });
  lines.push({ x1: xRight, y1: yRight, x2: xMeasure, y2: yRight, dashed: true });
  labels.push({ x: xMeasure, y: (yMin + yMax) / 2, text: formatMeasureDistance(distance) });
}

function rectsOverlapIn2D(source: RectEdges, target: RectEdges): boolean {
  return source.left < target.right - EPSILON
    && target.left < source.right - EPSILON
    && source.top < target.bottom - EPSILON
    && target.top < source.bottom - EPSILON;
}

function addOverlappingEdgeMeasurements(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  source: RectEdges,
  target: RectEdges,
): void {
  if (!rectsOverlapIn2D(source, target)) {
    return;
  }

  const measureY = overlapCenter(source.top, source.bottom, target.top, target.bottom, source.centerY);
  const measureX = overlapCenter(source.left, source.right, target.left, target.right, source.centerX);

  if (Math.abs(source.left - target.left) > EPSILON) {
    addInsetMeasurement(lines, labels, source.left, target.left, measureY, measureY, "horizontal");
  }
  if (Math.abs(source.right - target.right) > EPSILON) {
    addInsetMeasurement(lines, labels, source.right, target.right, measureY, measureY, "horizontal");
  }
  if (Math.abs(source.top - target.top) > EPSILON) {
    addInsetMeasurement(lines, labels, source.top, target.top, measureX, measureX, "vertical");
  }
  if (Math.abs(source.bottom - target.bottom) > EPSILON) {
    addInsetMeasurement(lines, labels, source.bottom, target.bottom, measureX, measureX, "vertical");
  }
}

function addInsetMeasurement(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  fromEdge: number,
  toEdge: number,
  spanStart: number,
  spanEnd: number,
  direction: "horizontal" | "vertical",
): void {
  const distance = Math.abs(toEdge - fromEdge);
  if (distance <= EPSILON) {
    return;
  }

  const spanCenter = (spanStart + spanEnd) / 2;
  if (direction === "horizontal") {
    lines.push({ x1: fromEdge, y1: spanCenter, x2: toEdge, y2: spanCenter });
    labels.push({ x: (fromEdge + toEdge) / 2, y: spanCenter, text: formatMeasureDistance(distance) });
    return;
  }

  lines.push({ x1: spanCenter, y1: fromEdge, x2: spanCenter, y2: toEdge });
  labels.push({ x: spanCenter, y: (fromEdge + toEdge) / 2, text: formatMeasureDistance(distance) });
}

// ─── Vector edit measurement ────────────────────────────────────────────────

function closestPointOnSegment(p: Point, a: Point, b: Point): Point {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-10) {
    return { x: a.x, y: a.y };
  }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

function closestPointOnEdge(
  point: Point,
  from: Point,
  to: Point,
  fromHandle: Point | undefined,
  toHandle: Point | undefined,
): Point {
  const samples = sampleEdgeBezier(from, to, fromHandle, toHandle, 32);
  let bestDist = Infinity;
  let bestPoint: Point = from;

  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i]!;
    const b = samples[i + 1]!;
    const candidate = closestPointOnSegment(point, a, b);
    const d = Math.hypot(candidate.x - point.x, candidate.y - point.y);
    if (d < bestDist) {
      bestDist = d;
      bestPoint = candidate;
    }
  }

  return bestPoint;
}

/**
 * Add an L-shaped measurement between two points: horizontal arm first, then
 * vertical arm, each labelled with its axis-aligned distance.
 * When one axis is zero, only the relevant arm is drawn.
 */
function addLShapeMeasurement(
  lines: MeasureOverlayLine[],
  labels: MeasureOverlayLabel[],
  from: Point,
  to: Point,
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  if (adx < EPSILON && ady < EPSILON) {
    return;
  }

  // Elbow corner: move horizontally from `from` first, then vertically to `to`
  const elbow: Point = { x: to.x, y: from.y };

  if (adx >= EPSILON && ady >= EPSILON) {
    // Both axes — draw full L with dashed helper line on the longer arm
    lines.push({ x1: from.x, y1: from.y, x2: elbow.x, y2: elbow.y });
    labels.push({ x: (from.x + elbow.x) / 2, y: elbow.y, text: formatMeasureDistance(adx) });

    lines.push({ x1: elbow.x, y1: elbow.y, x2: to.x, y2: to.y });
    labels.push({ x: elbow.x, y: (elbow.y + to.y) / 2, text: formatMeasureDistance(ady) });
  } else if (adx >= EPSILON) {
    lines.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y });
    labels.push({ x: (from.x + to.x) / 2, y: from.y, text: formatMeasureDistance(adx) });
  } else {
    lines.push({ x1: from.x, y1: from.y, x2: to.x, y2: to.y });
    labels.push({ x: from.x, y: (from.y + to.y) / 2, text: formatMeasureDistance(ady) });
  }
}

/**
 * Compute measurement overlay for vector edit mode.
 * All output coordinates are in document (world) space.
 * Uses L-shaped (axis-aligned) measurements, mirroring the normal measure overlay.
 * Cases:
 *  - selected vertices + Alt-hover vertex  → L-shape from each selected vertex to hovered vertex
 *  - selected vertices + Alt-hover edge    → L-shape from each selected vertex to nearest point on edge
 *  - exactly 2 selected vertices, no hover → L-shape between them
 */
export function computeVectorMeasureOverlay(
  document: CurvaDocument,
  nodeId: string,
  selectedVertexIds: string[],
  hoveredVertexId: string | undefined,
  hoveredEdgeId: string | undefined,
): MeasureOverlay | undefined {
  const node = findSceneNode(document, nodeId);
  if (!node || node.type !== "vectorNetwork") {
    return undefined;
  }

  const matrix = nodeSelectionLocalToWorldMatrix(document, nodeId);
  const toWorld = (p: Point): Point => applyAffinePoint(matrix, p);

  const vertexById = new Map(
    node.network.vertices.map(v => [v.id, toWorld(v)] as const),
  );
  const edgeById = new Map(node.network.edges.map(e => [e.id, e] as const));

  const selectedSet = new Set(selectedVertexIds);
  const selectedPositions = selectedVertexIds
    .map(id => vertexById.get(id))
    .filter((v): v is Point => v !== undefined);

  if (selectedPositions.length === 0) {
    return undefined;
  }

  const lines: MeasureOverlayLine[] = [];
  const labels: MeasureOverlayLabel[] = [];

  // Case 1: hovered vertex not in selection
  if (hoveredVertexId && !selectedSet.has(hoveredVertexId)) {
    const hoveredPos = vertexById.get(hoveredVertexId);
    if (hoveredPos) {
      for (const selPos of selectedPositions) {
        addLShapeMeasurement(lines, labels, selPos, hoveredPos);
      }
      if (lines.length > 0) {
        return { lines, labels };
      }
    }
  }

  // Case 2: hovered edge
  if (hoveredEdgeId) {
    const edge = edgeById.get(hoveredEdgeId);
    if (edge) {
      const fromPos = vertexById.get(edge.from.vertexId);
      const toPos = vertexById.get(edge.to.vertexId);
      if (fromPos && toPos) {
        const fromHandle = edge.from.handle ? toWorld(edge.from.handle) : undefined;
        const toHandle = edge.to.handle ? toWorld(edge.to.handle) : undefined;
        for (const selPos of selectedPositions) {
          const closest = closestPointOnEdge(selPos, fromPos, toPos, fromHandle, toHandle);
          addLShapeMeasurement(lines, labels, selPos, closest);
        }
        if (lines.length > 0) {
          return { lines, labels };
        }
      }
    }
  }

  // Case 3: no hover, exactly 2 selected vertices
  if (selectedPositions.length === 2) {
    const [a, b] = selectedPositions as [Point, Point];
    addLShapeMeasurement(lines, labels, a, b);
    return { lines, labels };
  }

  return undefined;
}
